import React, { useEffect, useState, useMemo } from "react";
import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  Box,
  Anchor,
  Divider,
  Paper,
  List,
  Loader,
  Center,
  Alert,
  Table,
  Select,
  Pagination,
  LoadingOverlay,
  Modal,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconDownload,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconMessage,
  IconCalendarEvent,
} from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { highlightVariables } from "../../utils/messageUtils";
import { useCampaign } from "../../context/CampaignContext";
import { useApp } from "../../context/AppContext";
import { formatDateTime } from "../../utils/timeUtils";
import { useSalon } from "../../context/SalonContext";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { toCSV, downloadCSV } from "../../utils/csvUtils";
import EditCampaignForm from "./EditCampaignForm";
import SummaryStrip from "../sales/shared/SummaryStrip";
import {
  SalesSearchInput,
  MenuToken,
  ResultCount,
} from "../sales/shared/salesToolbarParts";
import salesToolbarStyles from "../sales/shared/sales.module.css";
import TransactionDetailModal from "../calendar/CalendarView/components/TransactionDetailModal/TransactionDetailModal";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import moment from "moment-timezone";
import usePermissions from "../../hooks/usePermissions";

// Appointment-booked filter for the recipients toolbar pill.
const APPT_FILTER_OPTIONS = [
  { value: "all", label: "All recipients" },
  { value: "booked", label: "Booked an appointment" },
  { value: "not_booked", label: "No appointment" },
];

function ViewCampaign() {
  const perms = usePermissions();
  const marketingPerms = perms?.marketing;
  const { id } = useParams();
  const navigate = useNavigate();
  const { businessInfo } = useSalon();
  const {
    campaignDetails,
    campaignMessages,
    campaignLoading,
    campaignErrors,
    fetchCampaignDetails,
    updateCampaign,
    deleteCampaign,
  } = useCampaign();
  const { getTransaction } = useApp();

  // Per-recipient message-body preview + booked-appointment detail modal.
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [apptTxn, setApptTxn] = useState(null);
  // Transaction uuid currently being loaded — only that row's icon spins; the
  // others stay clickable.
  const [loadingApptTxn, setLoadingApptTxn] = useState(null);

  const handleViewAppointment = async (txnUuid) => {
    if (!txnUuid) return;
    try {
      setLoadingApptTxn(txnUuid);
      const txn = await getTransaction(txnUuid);
      setApptTxn(txn);
    } catch (err) {
      notifyError("Could not load the booked appointment.");
    } finally {
      setLoadingApptTxn(null);
    }
  };

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [hasChanged, setHasChanged] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Message list state
  const [messagePage, setMessagePage] = useState(1);
  const [messagePageSize, setMessagePageSize] = useState(10);
  const [messageStatusFilter, setMessageStatusFilter] = useState("");
  const [messageNameFilter, setMessageNameFilter] = useState("");
  // "all" | "booked" (has a linked appointment) | "not_booked".
  const [messageApptFilter, setMessageApptFilter] = useState("all");
  const [messageSortField, setMessageSortField] = useState("sent_at");
  const [messageSortDirection, setMessageSortDirection] = useState("desc");

  const activeFilterCount =
    (messageStatusFilter ? 1 : 0) +
    (messageNameFilter.trim() ? 1 : 0) +
    (messageApptFilter !== "all" ? 1 : 0);

  const handleSortByColumn = (field) => {
    if (messageSortField === field) {
      setMessageSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setMessageSortField(field);
      setMessageSortDirection("asc");
    }
    setMessagePage(1);
  };

  const renderSortIcon = (field) => {
    if (messageSortField !== field) {
      return <IconSelector size={14} style={{ opacity: 0.5 }} />;
    }
    return messageSortDirection === "asc" ? (
      <IconChevronUp size={14} />
    ) : (
      <IconChevronDown size={14} />
    );
  };

  const sortableHeaderStyle = {
    fontWeight: 500,
    cursor: "pointer",
    userSelect: "none",
  };

  const handleClearFilters = () => {
    setMessageStatusFilter("");
    setMessageNameFilter("");
    setMessageApptFilter("all");
    setMessagePage(1);
  };

  const statusFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [{ value: "", label: "All Statuses" }];
    (campaignMessages || []).forEach((message) => {
      if (message?.status && !seen.has(message.status)) {
        seen.add(message.status);
        options.push({
          value: message.status,
          label:
            message.status.charAt(0) + message.status.slice(1).toLowerCase(),
        });
      }
    });
    return options;
  }, [campaignMessages]);

  useEffect(() => {
    if (id) {
      fetchCampaignDetails(id);
    }
  }, [id, fetchCampaignDetails]);

  useEffect(() => {
    if (campaignDetails) {
      setFormData({
        name: campaignDetails.name || "",
        description: campaignDetails.description || "",
      });
    }
  }, [campaignDetails]);

  // Check if campaign is scheduled and in the future
  const isScheduledAndInFuture = useMemo(() => {
    if (
      !campaignDetails ||
      !campaignDetails.start_date ||
      !businessInfo?.timezone
    ) {
      return false;
    }

    const now = moment();
    const startDate = moment
      .utc(campaignDetails.start_date)
      .tz(businessInfo?.timezone);

    return startDate.isAfter(now);
  }, [campaignDetails, businessInfo?.timezone]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (campaignDetails) {
      const hasAnyChange =
        field === "name"
          ? campaignDetails.name !== value
          : campaignDetails.description !== value;
      setHasChanged(hasAnyChange);
    } else {
      setHasChanged(true);
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }
    if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }
    if (formData.description.trim().length > 256) {
      newErrors.description = "Description must be at most 256 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!hasChanged) {
      setIsEditing(false);
      return;
    }

    if (validateForm()) {
      const response = await updateCampaign(
        campaignDetails.uuid,
        formData.name,
        formData.description,
      );

      if (response) {
        notifySuccess("Campaign details updated successfully", {
          title: "Success!",
        });
        setIsEditing(false);
      } else {
        notifyError(
          campaignErrors.update || "Failed to update campaign details",
          { title: "Error" },
        );
      }
    }
  };

  const handleDelete = async () => {
    const response = await deleteCampaign(campaignDetails.uuid);

    if (response) {
      notifySuccess("Campaign deleted successfully", { title: "Success!" });
      navigate("/admin/marketing");
    } else {
      notifyError(campaignErrors.delete || "Failed to delete campaign", {
        title: "Error",
      });
    }
  };

  // Message filtering and sorting
  const filteredMessages = useMemo(() => {
    let result = [...campaignMessages];

    if (messageStatusFilter) {
      result = result.filter((msg) => msg.status === messageStatusFilter);
    }

    if (messageNameFilter.trim()) {
      const needle = messageNameFilter.trim().toLowerCase();
      result = result.filter((msg) => {
        const name = (msg.client_name || "").toLowerCase();
        const phone = `${msg.area_code || ""}${msg.phone_number || ""}`;
        return name.includes(needle) || phone.includes(needle);
      });
    }

    if (messageApptFilter !== "all") {
      const wantBooked = messageApptFilter === "booked";
      result = result.filter((msg) =>
        wantBooked
          ? !!msg.booked_transaction_uuid
          : !msg.booked_transaction_uuid,
      );
    }

    const direction = messageSortDirection === "asc" ? 1 : -1;
    const isMissing = (v) => v === null || v === undefined || v === "";
    result.sort((a, b) => {
      const aValue = a[messageSortField];
      const bValue = b[messageSortField];
      // Always push missing values to the bottom regardless of direction.
      if (isMissing(aValue) && isMissing(bValue)) return 0;
      if (isMissing(aValue)) return 1;
      if (isMissing(bValue)) return -1;

      let comparison;
      if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return comparison * direction;
    });

    return result;
  }, [
    campaignMessages,
    messageStatusFilter,
    messageNameFilter,
    messageApptFilter,
    messageSortField,
    messageSortDirection,
  ]);

  const handleExportCSV = () => {
    if (filteredMessages.length === 0) {
      notifyWarning("There are no recipients matching the current filters.", {
        title: "Nothing to export",
      });
      return;
    }

    const columns = [
      { label: "Client Name", key: "client_name" },
      {
        label: "Phone",
        value: (msg) =>
          formatPhoneNumber(`${msg.area_code || ""}${msg.phone_number || ""}`),
      },
      { label: "Area Code", key: "area_code" },
      { label: "Phone Number", key: "phone_number" },
      { label: "Status", key: "status" },
      {
        label: "Sent At",
        value: (msg) =>
          msg.sent_at
            ? formatDateTime(
                msg.sent_at,
                businessInfo?.timezone,
                "YYYY-MM-DD HH:mm:ss",
              )
            : "",
      },
      { label: "Failure Reason", key: "failure_reason" },
      { label: "Client ID", key: "client_id" },
    ];

    const csv = toCSV(filteredMessages, columns);
    const safeName = (campaignDetails?.name || "campaign")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const dateStamp = moment().format("YYYYMMDD");
    downloadCSV(`${safeName || "campaign"}-recipients-${dateStamp}.csv`, csv);
  };

  // Message pagination
  const totalMessagePages = Math.ceil(
    filteredMessages.length / messagePageSize,
  );
  const pagedMessages = filteredMessages.slice(
    (messagePage - 1) * messagePageSize,
    messagePage * messagePageSize,
  );

  if (campaignLoading.details) {
    return (
      <Center h={300}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (campaignErrors.details) {
    return (
      <Stack p="md">
        <Button variant="subtle" onClick={() => navigate("/admin/marketing")}>
          &larr; Back
        </Button>
        <Alert color="red" title="Error">
          {campaignErrors.details}
        </Alert>
      </Stack>
    );
  }

  if (!campaignDetails) {
    return null;
  }

  return (
    <Stack gap="md" p="md">
      <LoadingOverlay visible={campaignLoading.delete} />
      <Group justify="space-between">
        <Button
          variant="subtle"
          onClick={() => navigate("/admin/marketing")}
          style={{ alignSelf: "flex-start" }}
        >
          &larr; Back
        </Button>
        {isEditing ? (
          <Group>
            <Button variant="default" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={campaignLoading.update}>
              Save Changes
            </Button>
          </Group>
        ) : (
          <Group>
            {isScheduledAndInFuture && marketingPerms?.canCancel !== false && (
              <Button
                color="red"
                variant="subtle"
                onClick={() => setShowDeleteModal(true)}
                loading={campaignLoading.delete}
              >
                Delete
              </Button>
            )}
            {marketingPerms?.canCreate !== false && (
              <Button
                variant="outline"
                color="var(--brand-teal)"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </Group>
        )}
      </Group>
      <Title order={3}>Campaign Details</Title>

      {isEditing ? (
        <EditCampaignForm
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        />
      ) : (
        <Stack gap={4}>
          <Title order={4}>{campaignDetails.name}</Title>
          {campaignDetails.description && (
            <Text c="dimmed" size="sm">
              {campaignDetails.description}
            </Text>
          )}
          <Group gap="xl" mt={6} wrap="wrap">
            <Text size="sm" c="dimmed">
              <Text span fw={600} c="dark.6">
                Type:{" "}
              </Text>
              {campaignDetails.message_type}
            </Text>
            <Text size="sm" c="dimmed">
              <Text span fw={600} c="dark.6">
                Created:{" "}
              </Text>
              {formatDateTime(
                campaignDetails.created_dt,
                businessInfo?.timezone,
                "MMM D, YYYY h:mm A",
              )}
            </Text>
            <Text size="sm" c="dimmed">
              <Text span fw={600} c="dark.6">
                Scheduled:{" "}
              </Text>
              {formatDateTime(
                campaignDetails.start_date,
                businessInfo?.timezone,
                "MMM D, YYYY h:mm A",
              )}
            </Text>
            {campaignDetails.end_date && (
              <Text size="sm" c="dimmed">
                <Text span fw={600} c="dark.6">
                  Ends:{" "}
                </Text>
                {formatDateTime(
                  campaignDetails.end_date,
                  businessInfo?.timezone,
                  "MMM D, YYYY h:mm A",
                )}
              </Text>
            )}
          </Group>
        </Stack>
      )}

      {/* Sales-style stat boxes (mirrors SummaryStrip on the Sales/Client Groups
          pages): total sent, click rate, conversion rate, then the projected
          (Booked) and collected (Realized) revenue pair. */}
      <SummaryStrip
        stats={[
          {
            label: "Total Sent",
            value: String(campaignDetails.total_sent ?? 0),
          },
          {
            label: "Click Rate",
            value: `${((campaignDetails.click_rate || 0) * 100).toFixed(1)}%`,
          },
          {
            label: "Conversion Rate",
            value: `${((campaignDetails.conversion_rate || 0) * 100).toFixed(1)}%`,
          },
          {
            label: "Booked Revenue",
            value: formatCurrency(campaignDetails.booked_revenue),
            info: "Projected revenue from every appointment booked through this campaign that hasn't been cancelled, no-showed, or declined — including upcoming ones. Based on expected charges after discounts and prepaid package credits, excluding tips.",
          },
          {
            label: "Realized Revenue",
            value: formatCurrency(campaignDetails.revenue),
            info: "Revenue actually collected so far from this campaign's appointments: succeeded payments minus refunds, excluding tips. Becomes a subset of Booked Revenue as appointments are completed and paid.",
          },
        ]}
      />

      <Divider my="sm" />

      <Group justify="space-between" align="center">
        <Title order={4}>Messages</Title>
        <Button
          color="var(--brand-teal)"
          leftSection={<IconDownload size={16} />}
          onClick={handleExportCSV}
          disabled={
            campaignLoading.messages ||
            campaignMessages.length === 0 ||
            filteredMessages.length === 0
          }
        >
          Export CSV
        </Button>
      </Group>

      {/* Search bar + filter pills, mirroring the Sales transactions toolbar. */}
      <Box className={salesToolbarStyles.toolbar} mt="sm">
        <SalesSearchInput
          value={messageNameFilter}
          onChange={(v) => {
            setMessageNameFilter(v);
            setMessagePage(1);
          }}
          placeholder="Name or phone"
        />
        <MenuToken
          label="Status"
          valueLabel={
            statusFilterOptions.find((o) => o.value === messageStatusFilter)
              ?.label || "All Statuses"
          }
          current={messageStatusFilter}
          options={statusFilterOptions}
          onPick={(v) => {
            setMessageStatusFilter(v || "");
            setMessagePage(1);
          }}
        />
        <MenuToken
          label="Appointment"
          valueLabel={
            APPT_FILTER_OPTIONS.find((o) => o.value === messageApptFilter)
              ?.label || "All recipients"
          }
          current={messageApptFilter}
          options={APPT_FILTER_OPTIONS}
          onPick={(v) => {
            setMessageApptFilter(v || "all");
            setMessagePage(1);
          }}
        />
        {activeFilterCount > 0 && (
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="dimmed"
            onClick={handleClearFilters}
          >
            Clear all
          </Anchor>
        )}
        <Box style={{ flex: 1 }} />
        <ResultCount
          count={filteredMessages.length}
          total={campaignMessages.length}
          itemLabel="recipients"
        />
      </Box>

      {campaignLoading.messages ? (
        <Center h={200}>
          <Loader size="md" />
        </Center>
      ) : (
        <>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={sortableHeaderStyle}
                  onClick={() => handleSortByColumn("client_name")}
                >
                  <Group gap={4} wrap="nowrap">
                    <span>Client</span>
                    {renderSortIcon("client_name")}
                  </Group>
                </Table.Th>
                <Table.Th
                  style={sortableHeaderStyle}
                  onClick={() => handleSortByColumn("phone_number")}
                >
                  <Group gap={4} wrap="nowrap">
                    <span>Phone</span>
                    {renderSortIcon("phone_number")}
                  </Group>
                </Table.Th>
                <Table.Th
                  style={sortableHeaderStyle}
                  onClick={() => handleSortByColumn("status")}
                >
                  <Group gap={4} wrap="nowrap">
                    <span>Status</span>
                    {renderSortIcon("status")}
                  </Group>
                </Table.Th>
                <Table.Th
                  style={sortableHeaderStyle}
                  onClick={() => handleSortByColumn("sent_at")}
                >
                  <Group gap={4} wrap="nowrap">
                    <span>Sent At</span>
                    {renderSortIcon("sent_at")}
                  </Group>
                </Table.Th>
                <Table.Th
                  style={sortableHeaderStyle}
                  onClick={() => handleSortByColumn("failure_reason")}
                >
                  <Group gap={4} wrap="nowrap">
                    <span>Failure Reason</span>
                    {renderSortIcon("failure_reason")}
                  </Group>
                </Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pagedMessages.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      {campaignMessages.length === 0
                        ? "No recipients yet."
                        : "No recipients match the current filters."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                pagedMessages.map((message) => (
                  <Table.Tr key={message.uuid}>
                    <Table.Td>{message.client_name}</Table.Td>
                    <Table.Td>
                      {formatPhoneNumber(
                        `${message.area_code}${message.phone_number}`,
                      )}
                    </Table.Td>
                    <Table.Td>{message.status}</Table.Td>
                    <Table.Td>
                      {message.sent_at
                        ? formatDateTime(
                            message.sent_at,
                            businessInfo?.timezone,
                            "MMM D, YYYY h:mm A",
                          )
                        : "-"}
                    </Table.Td>
                    <Table.Td>{message.failure_reason || "-"}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label="View message" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="var(--brand-teal)"
                            aria-label="View message"
                            onClick={() => setSelectedMessage(message)}
                          >
                            <IconMessage size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          label={
                            message.booked_transaction_uuid
                              ? "View appointment"
                              : "No appointment booked"
                          }
                          withArrow
                        >
                          <ActionIcon
                            variant="subtle"
                            color="var(--brand-teal)"
                            aria-label="View appointment"
                            loading={
                              loadingApptTxn === message.booked_transaction_uuid
                            }
                            disabled={!message.booked_transaction_uuid}
                            onClick={() =>
                              handleViewAppointment(
                                message.booked_transaction_uuid,
                              )
                            }
                          >
                            <IconCalendarEvent size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          <Group justify="space-between" mt="md">
            <Select
              value={messagePageSize.toString()}
              onChange={(v) => {
                setMessagePageSize(Number(v));
                setMessagePage(1);
              }}
              data={[10, 20, 50].map((n) => ({
                value: n.toString(),
                label: n.toString(),
              }))}
              style={{ width: 100 }}
            />
            <Pagination
              page={messagePage}
              onChange={setMessagePage}
              total={totalMessagePages}
            />
          </Group>
        </>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign? This action cannot be undone."
      />

      <Modal
        opened={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        title="Sent message"
        centered
      >
        {selectedMessage && (
          <Paper withBorder p="md" radius="md">
            <Text
              size="sm"
              style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
            >
              {selectedMessage.message}
            </Text>
          </Paper>
        )}
      </Modal>

      {apptTxn && (
        <TransactionDetailModal
          opened={!!apptTxn}
          onClose={() => setApptTxn(null)}
          transaction={apptTxn}
          onTransactionUpdated={setApptTxn}
        />
      )}
    </Stack>
  );
}

export default ViewCampaign;
